import { useState, useContext, useEffect } from "@/common/hooks";
import {
  ErrorMessage,
  Spinner,
  Button,
  InputField,
  SuccessPopup,
} from "@/common/components";
import { useChangePasswordFromProfileMutation } from "@/service/ProfileService";
import { validate } from "@/utils/Validations";
import { PrivateContext } from "@/routes/PrivateRoute";
import { checkTwoFields, encryptData } from "@/utils/Utilities";

const ChangePassword = () => {
  const {
    debounceErrors,
    debounceState,
    cancel,
    setNavHeader,
    labels,
    setPageHeader,
  } = useContext(PrivateContext);
  const [showPopup, setShowPopup] = useState();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const [changePasswordApi, { isLoading: isFetching, error }] =
    useChangePasswordFromProfileMutation();

  const doChangePassword = async () => {
    const payload = {
      password: await encryptData(currentPassword),
      newPassword: await encryptData(newPassword),
      confirmPassword: await encryptData(confirmPassword),
    };
    changePasswordApi(payload)
      .unwrap()
      .then((res) => {
        if (!res.inError) {
          setShowPopup(true);
        }
      });
  };

  const checkDisabled = () => {
    let disable = false;

    Object.keys(errors).forEach((e) => {
      if (errors[e]) {
        disable = true;
      }
    });

    if (disable) {
      return true;
    }

    return !(currentPassword && newPassword && confirmPassword);
  };

  useEffect(() => {
    setNavHeader(labels?.navbar?.profile);
    setPageHeader(labels?.changePassword?.title);
  }, []);

  useEffect(() => {
    debounceErrors(
      {
        ...errors,
        passwordMatch: checkTwoFields(newPassword, confirmPassword, true)
          ? labels?.changePassword?.passwordMatch
          : "",
      },
      setErrors
    );
  }, [newPassword, confirmPassword]);

  return (
    <section className="container-medium py-5">
      {isFetching && <Spinner />}
      <ErrorMessage error={error} />

      <div className="password-info-box mb-5">
        <h6>YOUR PASSWORD</h6>
        <ul>
          <li>Must use at least 1 letter.</li>
          <li>Must use at least 1 number.</li>
          <li>Must contain between 7 &amp; 20 characters.</li>
          <li>In case-sensitive</li>
        </ul>
      </div>

      <InputField
        id="currentPassword"
        type="password"
        maxLength="20"
        label={labels?.changePassword?.currentPassword}
        value={currentPassword}
        change={async (e) => {
          debounceState(e, setCurrentPassword);
          debounceErrors(
            {
              ...errors,
              currentPassword: await validate(e, "password7"),
            },
            setErrors
          );
        }}
        touch={() => setTouched({ ...touched, currentPassword: true })}
        touched={touched.currentPassword}
        error={errors.currentPassword}
      />

      <InputField
        id="newPassword"
        type="password"
        maxLength="20"
        label={labels?.changePassword?.newPassword}
        value={newPassword}
        change={async (e) => {
          debounceState(e, setNewPassword);
          debounceErrors(
            {
              ...errors,
              newPassword: await validate(e, "password7"),
            },
            setErrors
          );
        }}
        touch={() => setTouched({ ...touched, newPassword: true })}
        touched={touched.newPassword}
        error={errors.newPassword}
      />

      <InputField
        id="confirmPassword"
        type="password"
        maxLength="20"
        label={labels?.changePassword?.confirmPassword}
        value={confirmPassword}
        change={async (e) => {
          debounceState(e, setConfirmPassword);
          debounceErrors(
            {
              ...errors,
              confirmPassword: await validate(e, "confirmPassword"),
            },
            setErrors
          );
        }}
        touch={() => setTouched({ ...touched, confirmPassword: true })}
        touched={touched.confirmPassword}
        error={errors.confirmPassword || errors.passwordMatch}
      />

      <Button
        id="changePassword"
        text={labels?.changePassword?.changePassword}
        wrapperClassName="col-md-4 mt-5 ms-auto"
        click={doChangePassword}
        disabled={checkDisabled()}
      />

      <SuccessPopup
        showModal={showPopup}
        buttonText={labels?.changePassword?.continue}
        click={cancel}
        body={labels?.changePassword?.success}
      />
    </section>
  );
};

export default ChangePassword;
